import * as React from "react";

import type { EventRecord } from "@/shared/domain/events";

import { SidebarViewType, useWorkspaceSidebar } from "../../../sidebarContext";
import { type Timer, useTimerState } from "../../../timer";

import { selectActiveEvents } from "./eventFilters";
import { EventPill } from "./EventPill";
import styles from "./ImagePanelEvents.module.css";

interface ActiveEventPillsProps {
  events: EventRecord[];
  timer: Timer;
}

/**
 * Renders a pill for each of `events` active at the current playhead time;
 * clicking a pill opens the event's details in the sidebar.
 *
 * Reading the playhead re-renders this on every tick during playback, so it is
 * kept as a leaf: only the pills re-render, not the whole panel.
 */
export function ActiveEventPills({ events, timer }: ActiveEventPillsProps) {
  const { timeNs } = useTimerState(timer);
  const sidebar = useWorkspaceSidebar();

  const activeEvents = React.useMemo(
    () => selectActiveEvents(events, timeNs),
    [events, timeNs],
  );

  if (activeEvents.length === 0) {
    return null;
  }

  return (
    <div className={styles.pillRow} data-not-draggable="true">
      {activeEvents.map((event) => (
        <EventPill
          key={event.event_id}
          name={event.name}
          color={event.display_options?.color}
          onClick={() =>
            sidebar.openView({
              type: SidebarViewType.EventDetails,
              data: { eventId: event.event_id },
            })
          }
        />
      ))}
    </div>
  );
}
